import React from "react";
import { DollarSign, Briefcase, Users, Receipt, Activity } from "lucide-react";
import { useNexOs, formatKpi } from "@/lib/nexOs";
import { useLabel, Section } from "@/lib/dynamicEngine";
import { useNdsEditor, useTileHandlers } from "@/lib/ndsEditor";

const ICONS = {
  dollar: DollarSign,
  briefcase: Briefcase,
  users: Users,
  receipt: Receipt,
  default: Activity,
};

/**
 * Tier 1 — Real-Time KPI Pulse Bar.
 * 4 uniform metric cards with an icon badge in the top-right of each card.
 * Every card is wrapped in <Section> so it can be individually hidden/reordered
 * from /admin/layout without a code change. In Edit Mode, single-click opens
 * the Quick-Edit modal; double-click escalates to the Workbench overlay.
 */
export default function KpiPulseBar() {
  const { kpis, currentVertical } = useNexOs();
  const kicker = useLabel("nds.kpi.kicker", "// REAL-TIME KPI PULSE");
  const liveTag = useLabel("nds.kpi.live_tag", "LIVE");
  const demoTag = useLabel("nds.kpi.demo_tag", "DEMO");

  return (
    <div className="flex flex-col gap-4" data-testid="nds-kpi-bar">
      <div className="flex items-baseline justify-between">
        <div className="nds-label-dim">{kicker}</div>
        {currentVertical && (
          <div className="font-mono text-[0.7rem] text-nex-chrome tracking-[0.18em] uppercase">
            {currentVertical.label}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = ICONS[k.icon] || ICONS.default;
          const target = currentVertical ? { kind: "kpi", verticalId: currentVertical.id, itemId: k.id } : null;
          return (
            <Section scope="nds_kpi" id={k.id} key={k.id}>
              <KpiCard k={k} Icon={Icon} target={target} liveTag={liveTag} demoTag={demoTag} />
            </Section>
          );
        })}
        {kpis.length === 0 && (
          <div className="col-span-4 nds-card p-8 text-center font-mono text-sm text-nex-chrome" data-testid="nds-kpi-empty">
            Loading KPIs…
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ k, Icon, target, liveTag, demoTag }) {
  const { openTileEditor } = useNdsEditor();
  const tileHandlers = useTileHandlers({ target, onEdit: openTileEditor });
  return (
    <div
      {...tileHandlers}
      className="nds-card p-5 relative transition-all duration-200"
      data-testid={`nds-kpi-card-${k.id}`}
      title="Right-click to edit"
    >
      <div className="flex items-start justify-between">
        <div className="nds-label">{k.label}</div>
        <div className="w-9 h-9 grid place-items-center bg-nex-navy text-nex-goldbright rounded-sm">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
      </div>
      <div
        className="mt-4 text-3xl md:text-4xl nds-num tabular-nums"
        data-testid={`nds-kpi-value-${k.id}`}
      >
        {formatKpi(k.value, k.format)}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span
          className={`nds-pill ${k.is_live ? "nds-pill--live" : "nds-pill--queued"}`}
          style={{ padding: "3px 8px", fontSize: "0.625rem" }}
          data-testid={`nds-kpi-tag-${k.id}`}
        >
          {k.is_live ? liveTag : demoTag}
        </span>
        <span className="font-mono text-[0.65rem] text-nex-muted tracking-widest uppercase">
          · trailing 30d
        </span>
      </div>
    </div>
  );
}
